import { useEffect, useMemo, useState } from 'react';
import { WRITE_IN_MAX } from '@shared/rules';
import type { CardData, PublicSubmission, SubmitChoice } from '@shared/types';
import { AnswerCard, FaceDownCard, FilledPrompt, PromptCard } from '../../components/Cards';
import { Timer, useCountdown } from '../../components/Chrome';
import { usePrefs } from '../../components/Prefs';
import { Sheet } from '../../components/Sheet';
import { ShareWinButton } from '../../components/ShareWinButton';
import type { RoomApi } from '../../lib/useRoom';
import { Choosing, GuessResults, Guessing, RoundTags, WorstBanner, Writing, names } from './Modes';

type Pick = { kind: 'card'; card: CardData } | { kind: 'write'; text: string };

function pickText(p: Pick) {
  return p.kind === 'card' ? p.card.text : p.text;
}

export function Play({ room }: { room: RoomApi }) {
  const view = room.view!;
  const round = view.round!;
  const seconds = useCountdown(round.deadline, room.serverNow);

  return (
    <>
      {view.phase === 'choosing' && <Choosing room={room} seconds={seconds} />}
      {view.phase === 'writing' && <Writing room={room} seconds={seconds} />}
      {view.phase === 'submitting' && <Submitting room={room} seconds={seconds} />}
      {view.phase === 'judging' && <Judging room={room} seconds={seconds} />}
      {view.phase === 'guessing' && <Guessing room={room} seconds={seconds} />}
      {view.phase === 'round_over' && <RoundOver room={room} />}
    </>
  );
}

// ------------------------------------------------------------------ submitting

function Submitting({ room, seconds }: { room: RoomApi; seconds: number | null }) {
  const view = room.view!;
  const round = view.round!;
  const me = view.me!;
  const { toast } = usePrefs();
  const [picks, setPicks] = useState<Pick[]>([]);
  const [writing, setWriting] = useState(false);
  const [suggestWriteIn, setSuggestWriteIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const isJudge = round.judgeId === me.id;
  const judge = view.players.find((p) => p.id === round.judgeId);
  const waitingOn = view.players.filter((p) => p.connected && !p.done && p.id !== round.judgeId);
  const pick = round.prompt.pick;
  const writeInsUsed = picks.filter((p) => p.kind === 'write').length;

  // New round: clear the selection.
  useEffect(() => {
    setPicks([]);
    setSuggestWriteIn(false);
  }, [round.number, round.prompt.id]);

  function toggle(card: CardData) {
    setPicks((current) => {
      const at = current.findIndex((p) => p.kind === 'card' && p.card.id === card.id);
      if (at >= 0) return current.filter((_, i) => i !== at);
      if (current.length < pick) return [...current, { kind: 'card', card }];
      if (pick === 1) return [{ kind: 'card', card }];
      return current;
    });
  }

  async function submit() {
    setBusy(true);
    const choices: SubmitChoice[] = picks.map((p) => (p.kind === 'card' ? { cardId: p.card.id } : { writeIn: p.text }));
    const err = await room.act('round:submit', { choices, suggestWriteIn });
    setBusy(false);
    if (err) toast(err, 'error');
  }

  async function redraw() {
    if (!window.confirm(`Swap your whole hand for ${me.hand.length} new cards? You have ${me.redrawsLeft} swap${me.redrawsLeft === 1 ? '' : 's'} left this game.`)) return;
    setPicks([]);
    const err = await room.act('round:redraw', {});
    if (err) toast(err, 'error');
  }

  const orderOf = (card: CardData) => {
    const i = picks.findIndex((p) => p.kind === 'card' && p.card.id === card.id);
    return i >= 0 ? i + 1 : undefined;
  };

  return (
    <>
      <main className="game-main">
        {round.notice && <p className="notice">{round.notice}</p>}
        <div className="row between">
          <span className="status-line">
            {isJudge ? "👑 You're the judge" : judge ? `👑 ${judge.name} is judging` : '🗳️ Everyone plays, everyone votes'}
          </span>
          <Timer seconds={seconds} />
        </div>
        {round.worst && <WorstBanner stage={isJudge ? 'watch' : 'play'} />}
        <RoundTags round={round} />
        <PromptCard card={round.prompt} answers={picks.length && !isJudge ? picks.map(pickText) : undefined} className="pop-in" />

        {isJudge ? (
          <section className="panel stack center">
            <h2>Read the prompt out loud</h2>
            <p className="muted" style={{ margin: 0 }}>
              {waitingOn.length ? `Waiting for ${names(waitingOn)} to play.` : 'Everyone has played. Shuffling...'}
            </p>
          </section>
        ) : me.submitted ? (
          <section className="panel stack center">
            <h2>Your card is in 🙌</h2>
            <p className="muted" style={{ margin: 0 }}>
              {waitingOn.length ? `Waiting for ${names(waitingOn)}.` : 'Everyone has played.'}
            </p>
          </section>
        ) : (
          <section className="stack">
            <div className="row between">
              <strong>
                {pick > 1 ? `Pick ${pick} cards, in order` : round.worst ? 'Pick your most bekaar card' : 'Pick your funniest card'}
                {picks.length > 0 && <span className="muted"> · {picks.length}/{pick}</span>}
              </strong>
              <div className="row">
                {me.writeInsLeft - writeInsUsed > 0 && picks.length < pick && (
                  <button type="button" className="btn btn-sm btn-outline" onClick={() => setWriting(true)}>
                    ✍️ Write my own ({me.writeInsLeft - writeInsUsed})
                  </button>
                )}
                {me.redrawsLeft > 0 && (
                  <button type="button" className="btn btn-sm btn-ghost" onClick={redraw}>
                    🔄 Swap hand
                  </button>
                )}
              </div>
            </div>
            {picks.some((p) => p.kind === 'write') && (
              <div className="stack" style={{ gap: 8 }}>
                {picks.map((p, i) =>
                  p.kind === 'write' ? (
                    <AnswerCard
                      key={`w${i}`}
                      card={{ id: `w${i}`, type: 'answer', text: p.text, pick: 1, writeIn: true }}
                      selected
                      order={pick > 1 ? i + 1 : undefined}
                      onClick={() => setPicks((cur) => cur.filter((_, j) => j !== i))}
                    />
                  ) : null,
                )}
                <label className="check small">
                  <input type="checkbox" checked={suggestWriteIn} onChange={(e) => setSuggestWriteIn(e.target.checked)} />
                  Also suggest my blank card for the official deck
                </label>
              </div>
            )}
            <div className="hand">
              {me.hand.map((card) => (
                <AnswerCard key={card.id} card={card} selected={Boolean(orderOf(card))} order={pick > 1 ? orderOf(card) : undefined} onClick={() => toggle(card)} />
              ))}
            </div>
          </section>
        )}
      </main>

      {!isJudge && !me.submitted && (
        <div className="action-bar">
          <div className="inner">
            <button className="btn btn-primary btn-lg btn-block" disabled={picks.length !== pick || busy} onClick={submit}>
              {busy ? 'Playing...' : picks.length === pick ? `Play ${pick > 1 ? 'these cards' : 'this card'}` : `Choose ${pick - picks.length} more`}
            </button>
          </div>
        </div>
      )}

      {writing && (
        <WriteInSheet
          onClose={() => setWriting(false)}
          onSave={(text) => {
            const card: Pick = { kind: 'write', text };
            setPicks((cur) => (pick === 1 ? [card] : [...cur, card].slice(0, pick)));
            setWriting(false);
          }}
        />
      )}
    </>
  );
}

function WriteInSheet({ onClose, onSave }: { onClose: () => void; onSave: (text: string) => void }) {
  const [text, setText] = useState('');
  return (
    <Sheet title="Write your own card" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) onSave(text.trim());
        }}
      >
        <p className="muted small" style={{ margin: 0 }}>
          This uses one of your blank cards for the game. Keep it short and specific.
        </p>
        <textarea className="input" autoFocus maxLength={WRITE_IN_MAX} value={text} onChange={(e) => setText(e.target.value)} placeholder="Papa's 40-minute voice note" />
        <span className="muted small">
          {text.length}/{WRITE_IN_MAX}
        </span>
        <button className="btn btn-primary btn-lg" disabled={!text.trim()}>
          Use this card
        </button>
      </form>
    </Sheet>
  );
}

// ------------------------------------------------------------------ judging

function SubmissionCards({ sub, pick, onClick, selected, label }: { sub: PublicSubmission; pick: number; onClick?: () => void; selected?: boolean; label?: string }) {
  if (!sub.cards) return <FaceDownCard onClick={onClick} label={label} />;
  if (sub.cards.length === 1) {
    return <AnswerCard card={sub.cards[0]} selected={selected} onClick={onClick} className="flip-in" />;
  }
  return (
    <div className="combo" onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}>
      {sub.cards.map((c, i) => (
        <AnswerCard key={c.id} card={c} selected={selected} order={pick > 1 ? i + 1 : undefined} className="flip-in" />
      ))}
    </div>
  );
}

function Judging({ room, seconds }: { room: RoomApi; seconds: number | null }) {
  const view = room.view!;
  const round = view.round!;
  const me = view.me!;
  const { toast } = usePrefs();
  const voting = view.settings.judgeMode === 'vote';
  const isJudge = round.judgeId === me.id;
  const judge = view.players.find((p) => p.id === round.judgeId);
  const [chosen, setChosen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const allRevealed = round.submissions.every((s) => s.cards);
  const chosenSub = round.submissions.find((s) => s.id === chosen);
  const waitingVotes = view.players.filter((p) => p.connected && !p.done);

  useEffect(() => setChosen(null), [round.number]);

  async function act(event: 'round:reveal' | 'round:pick' | 'round:vote', submissionId: string) {
    const err = await room.act(event, { submissionId });
    if (err) toast(err, 'error');
  }

  const preview = isJudge && chosenSub?.cards ? chosenSub.cards.map((c) => c.text) : voting && me.votedFor ? round.submissions.find((s) => s.id === me.votedFor)?.cards?.map((c) => c.text) : undefined;

  let status: string;
  const best = round.worst ? 'worst' : 'funniest';
  if (voting) status = me.votedFor ? `Vote locked in. Waiting for ${names(waitingVotes) || 'the count'}.` : `Vote for the ${best} one (not ${view.teams.length ? "your team's" : 'yours'}!)`;
  else if (isJudge) status = allRevealed ? (round.worst ? 'Tap the worst one, then crown it' : 'Tap your favourite, then crown it') : 'Tap each card to flip it and read it out loud';
  else status = `${judge?.name ?? 'The judge'} is reading the cards...`;

  return (
    <>
      <main className="game-main">
        <div className="row between">
          <span className="status-line">{status}</span>
          <Timer seconds={seconds} />
        </div>
        {round.worst && <WorstBanner stage={voting ? 'vote' : isJudge ? 'judge' : 'watch'} />}
        <RoundTags round={round} />
        <PromptCard card={round.prompt} answers={preview} />
        <div className={`submissions ${round.prompt.pick > 1 ? 'multi' : ''}`}>
          {round.submissions.map((sub) => {
            let onClick: (() => void) | undefined;
            if (isJudge && !voting) onClick = sub.cards ? () => setChosen(sub.id) : () => act('round:reveal', sub.id);
            if (voting && !sub.mine && !sub.teammate) onClick = () => act('round:vote', sub.id);
            const selected = (isJudge && chosen === sub.id) || (voting && me.votedFor === sub.id);
            return (
              <div key={sub.id} className={`submission ${voting && me.votedFor === sub.id ? 'voted' : ''}`}>
                <SubmissionCards sub={sub} pick={round.prompt.pick} onClick={onClick} selected={selected} />
                {sub.mine && <span className="chip orange" style={{ alignSelf: 'flex-start' }}>yours</span>}
                {sub.teammate && <span className="chip blue" style={{ alignSelf: 'flex-start' }}>your team</span>}
              </div>
            );
          })}
        </div>
      </main>
      {isJudge && !voting && (
        <div className="action-bar">
          <div className="inner">
            {allRevealed ? (
              <button
                className="btn btn-primary btn-lg btn-block"
                disabled={!chosen || busy}
                onClick={async () => {
                  if (!chosen) return;
                  setBusy(true);
                  await act('round:pick', chosen);
                  setBusy(false);
                }}
              >
                {chosen ? '👑 Crown this one' : 'Tap a card to choose'}
              </button>
            ) : (
              <button
                className="btn btn-outline btn-lg btn-block"
                onClick={() => {
                  const next = round.submissions.find((s) => !s.cards);
                  if (next) void act('round:reveal', next.id);
                }}
              >
                Flip the next card
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ------------------------------------------------------------------ round over

function RoundOver({ room }: { room: RoomApi }) {
  const view = room.view!;
  const round = view.round!;
  const me = view.me!;
  const { toast } = usePrefs();
  const [busy, setBusy] = useState(false);
  const voting = view.settings.judgeMode === 'vote';
  const winners = round.submissions.filter((s) => s.isWinner);
  const canAdvance = voting || me.id === round.judgeId || me.id === view.hostId;
  const judge = view.players.find((p) => p.id === round.judgeId);
  const online = view.players.filter((p) => p.connected).length;
  const iWon = winners.some((w) => w.playerId === me.id);
  const sorted = useMemo(() => [...round.submissions].sort((a, b) => Number(b.isWinner) - Number(a.isWinner) || (b.votes ?? 0) - (a.votes ?? 0)), [round.submissions]);

  return (
    <>
      <main className="game-main">
        {winners.length > 0 ? (
          <div className="winner-banner pop-in">
            <div>
              {round.worst ? '🥴 ' : '🏆 '}
              {round.worst ? (iWon ? 'Your card was the most bekaar!' : 'Sabse Bekaar award') : iWon ? 'You won this round!' : 'Round winner'}
            </div>
            <div className="who">{winners.map((w) => w.playerName).join(' & ')}</div>
          </div>
        ) : (
          <p className="notice">{round.notice ?? 'No winner this round.'}</p>
        )}
        {winners.length > 0 && round.notice && <p className="notice">{round.notice}</p>}
        {winners[0]?.cards && <PromptCard card={round.prompt} answers={winners[0].cards.map((c) => c.text)} />}
        {winners[0]?.cards && (
          <div className="share-row">
            <ShareWinButton win={{ prompt: round.prompt, answers: winners[0].cards.map((c) => c.text), winner: winners.map((w) => w.playerName).join(' & '), kind: 'round', roundNumber: round.number }} />
          </div>
        )}
        {!winners.length && <PromptCard card={round.prompt} />}
        <RoundTags round={round} />
        <GuessResults view={view} />
        <h3 style={{ marginBottom: 0 }}>Everyone's cards</h3>
        <div className="stack">
          {sorted.map((sub) => (
            <div key={sub.id} className={`submission recap-item ${sub.isWinner ? 'winner' : ''}`}>
              <div className="by">
                {sub.isWinner && '🏆'} {sub.playerName}
                {voting && sub.votes !== null && <span className="chip">{sub.votes} vote{sub.votes === 1 ? '' : 's'}</span>}
              </div>
              {sub.cards && <FilledPrompt prompt={round.prompt} answers={sub.cards.map((c) => c.text)} />}
            </div>
          ))}
        </div>
      </main>
      <div className="action-bar">
        <div className="inner">
          {online < view.minPlayers ? (
            <p className="status-line center grow" style={{ margin: 0 }}>
              Waiting for at least {view.minPlayers} players to continue...
            </p>
          ) : canAdvance ? (
            <button
              className="btn btn-primary btn-lg btn-block"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                const err = await room.act('round:next', {});
                setBusy(false);
                if (err) toast(err, 'error');
              }}
            >
              Next round →
            </button>
          ) : (
            <p className="status-line center grow" style={{ margin: 0 }}>
              Waiting for {judge?.name ?? 'the judge'} to deal the next round...
            </p>
          )}
        </div>
      </div>
    </>
  );
}

